import { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";

const BUBBLE_BG = "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/rgr60lvb_ldsartbot_poodles_having_bubbles_play_time_--ar_9151_--v_7_66532452-890a-46e2-b4fe-86dffe398d84_2.png";

const PASTEL_COLORS = [
  "rgba(255, 182, 193, 0.45)",
  "rgba(255, 218, 185, 0.45)",
  "rgba(255, 255, 186, 0.45)",
  "rgba(186, 255, 201, 0.45)",
  "rgba(186, 225, 255, 0.45)",
  "rgba(221, 186, 255, 0.45)",
  "rgba(255, 186, 243, 0.45)",
  "rgba(186, 255, 255, 0.45)",
];

const GAME_DURATION = 60;

const BubblePopGame = ({ isOpen, onClose }) => {
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [gameState, setGameState] = useState('ready');
  const [popEffects, setPopEffects] = useState([]);
  const containerRef = useRef(null);
  const bubbleIdRef = useRef(0);
  const spawnRateRef = useRef(800);

  const spawnBubble = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const size = 55 + Math.random() * 65;
    const x = Math.random() * (rect.width - size);
    const color = PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)];
    const speed = 1.2 + Math.random() * 1.8;
    const wobble = Math.random() * 20 - 10;

    bubbleIdRef.current += 1;
    const newBubble = {
      id: bubbleIdRef.current,
      x,
      y: rect.height + size,
      size,
      color,
      speed,
      wobble,
      opacity: 0.7 + Math.random() * 0.3,
    };
    setBubbles(prev => [...prev, newBubble]);
  }, []);

  const popBubble = useCallback((id, x, y) => {
    setBubbles(prev => prev.filter(b => b.id !== id));
    setScore(prev => {
      const newScore = prev + 1;
      // Speed up spawning as score increases
      spawnRateRef.current = Math.max(200, 800 - newScore * 10);
      return newScore;
    });
    // Pop effect
    const effectId = Date.now() + Math.random();
    setPopEffects(prev => [...prev, { id: effectId, x, y }]);
    setTimeout(() => {
      setPopEffects(prev => prev.filter(e => e.id !== effectId));
    }, 500);
  }, []);

  // Game timer
  useEffect(() => {
    if (gameState !== 'playing') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameState('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState]);

  // Spawn bubbles
  useEffect(() => {
    if (gameState !== 'playing') return;
    const spawn = () => {
      spawnBubble();
    };
    spawn();
    const interval = setInterval(spawn, spawnRateRef.current);
    // Re-create interval when spawn rate changes
    const rateCheck = setInterval(() => {
      clearInterval(interval);
    }, 100);
    
    return () => {
      clearInterval(interval);
      clearInterval(rateCheck);
    };
  }, [gameState, spawnBubble]);

  // Continuous spawning with dynamic rate
  useEffect(() => {
    if (gameState !== 'playing') return;
    let timeoutId;
    const scheduleSpawn = () => {
      timeoutId = setTimeout(() => {
        spawnBubble();
        scheduleSpawn();
      }, spawnRateRef.current);
    };
    scheduleSpawn();
    return () => clearTimeout(timeoutId);
  }, [gameState, spawnBubble]);

  // Animate bubbles floating up
  useEffect(() => {
    if (gameState !== 'playing') return;
    const animate = setInterval(() => {
      setBubbles(prev => prev
        .map(b => ({
          ...b,
          y: b.y - b.speed,
          x: b.x + Math.sin(b.y / 30) * b.wobble * 0.05,
        }))
        .filter(b => b.y > -b.size)
      );
    }, 16);
    return () => clearInterval(animate);
  }, [gameState]);

  const startGame = () => {
    setBubbles([]);
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setPopEffects([]);
    spawnRateRef.current = 800;
    bubbleIdRef.current = 0;
    setGameState('playing');
  };

  const getMessage = () => {
    if (score >= 80) return "AMAZING! You're a bubble-popping superstar!";
    if (score >= 50) return "Wow, brilliant! You popped so many!";
    if (score >= 30) return "Great job! That was really fun!";
    if (score >= 15) return "Well done! You did really well!";
    return "That was fun! Want to try again?";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm">
      <div className="fixed inset-0 z-[80] flex flex-col" data-testid="bubble-pop-game">
        {/* Background */}
        <div className="absolute inset-0">
          <img src={BUBBLE_BG} alt="" className="w-full h-full object-cover object-top" />
        </div>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-md">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-[#4A4A4A]">Bubble Pop!</h2>
          </div>
          <div className="flex items-center gap-4">
            {gameState === 'playing' && (
              <>
                <div className="text-center">
                  <p className="text-xs text-[#9A9A9A]">Score</p>
                  <p className="text-xl font-bold text-[#B89823]">{score}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-[#9A9A9A]">Time</p>
                  <p className={`text-xl font-bold ${timeLeft <= 10 ? 'text-[#A55B64] animate-pulse' : 'text-[#4A4A4A]'}`}>
                    {timeLeft}s
                  </p>
                </div>
              </>
            )}
            <button onClick={onClose} className="p-2 rounded-full bg-white/80 hover:bg-white transition-colors" data-testid="bubble-pop-close">
              <X className="w-6 h-6 text-[#4A4A4A]" />
            </button>
          </div>
        </div>

        {/* Game Area */}
        <div
          ref={containerRef}
          className="relative flex-1 overflow-hidden cursor-pointer select-none"
          style={{ touchAction: 'none' }}
        >
          {/* Bubbles */}
          {bubbles.map(bubble => (
            <div
              key={bubble.id}
              onClick={(e) => {
                e.stopPropagation();
                popBubble(bubble.id, bubble.x + bubble.size / 2, bubble.y + bubble.size / 2);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                popBubble(bubble.id, bubble.x + bubble.size / 2, bubble.y + bubble.size / 2);
              }}
              className="absolute rounded-full cursor-pointer transition-transform hover:scale-110 active:scale-90"
              style={{
                left: bubble.x,
                top: bubble.y,
                width: bubble.size,
                height: bubble.size,
                background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8), ${bubble.color})`,
                border: '1px solid rgba(255,255,255,0.6)',
                boxShadow: `inset -2px -2px 6px rgba(0,0,0,0.05), 0 2px 8px rgba(0,0,0,0.08)`,
                opacity: bubble.opacity,
              }}
              data-testid="bubble"
            >
              <div
                className="absolute rounded-full bg-white/60"
                style={{
                  width: bubble.size * 0.25,
                  height: bubble.size * 0.15,
                  top: bubble.size * 0.2,
                  left: bubble.size * 0.2,
                  transform: 'rotate(-30deg)',
                  borderRadius: '50%',
                }}
              />
            </div>
          ))}

          {/* Pop Effects */}
          {popEffects.map(effect => (
            <div
              key={effect.id}
              className="absolute pointer-events-none animate-ping"
              style={{
                left: effect.x - 15,
                top: effect.y - 15,
                width: 30,
                height: 30,
              }}
            >
              <span className="text-2xl">POP!</span>
            </div>
          ))}

          {/* Ready Screen */}
          {gameState === 'ready' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 max-w-xs w-full text-center shadow-2xl mx-4">
                <p className="text-5xl mb-4">🫧</p>
                <h3 className="text-2xl font-bold text-[#4A4A4A] mb-2">Bubble Pop!</h3>
                <p className="text-sm text-[#6B6B6B] mb-6">
                  Pop as many bubbles as you can in 60 seconds!
                </p>
                <button
                  onClick={startGame}
                  data-testid="bubble-start-button"
                  className="px-8 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-bold text-lg hover:bg-[#D9C599] transition-all hover:scale-105"
                >
                  Start!
                </button>
              </div>
            </div>
          )}

          {/* Finished Screen */}
          {gameState === 'finished' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 max-w-xs w-full text-center shadow-2xl mx-4">
                <p className="text-5xl mb-4">🎉</p>
                <h3 className="text-2xl font-bold text-[#4A4A4A] mb-2">Time's Up!</h3>
                <p className="text-4xl font-bold text-[#B89823] my-4">{score}</p>
                <p className="text-sm text-[#6B6B6B] mb-2">bubbles popped!</p>
                <p className="text-sm font-medium text-[#4A4A4A] mb-6">{getMessage()}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={startGame}
                    data-testid="bubble-play-again"
                    className="px-6 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all hover:scale-105"
                  >
                    Play Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-[#F3F0E6] text-[#6B6B6B] rounded-full font-semibold hover:bg-[#E8E5DC] transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BubblePopGame;
